import { ReactNode, createContext, useContext, useState } from 'react';
import { WidgetId } from 'types/idTypes';
import { InfographState } from 'types/infographTypes';
import { WidgetType } from 'types/widget.types';
import { getWordCloudData } from 'widgets/ChartWidgets/WordCloud/WordCloud.helpers';
import { CloudWord } from 'widgets/ChartWidgets/WordCloud/WordCloud.types';
import { COLOR_STEPS } from 'widgets/ChartWidgets/WordCloud/WordCloudWidget.config';
import { WordCloudWidgetData } from 'widgets/ChartWidgets/WordCloud/WordCloudWidget.types';
import { calculateWordCloudPostions } from 'widgets/ChartWidgets/WordCloud/calculateExportWordCloudData';
import { getAllPageWidgetIds, getWidgetTypeFromId } from 'widgets/Widget.helpers';

export type WordCloudExportPositionData = { [id: WidgetId]: CloudWord[] };

export const WordCloudExportContext = createContext<WordCloudExportPositionData>({});
export const WordCloudExportProvider = ({ children }: { children: ReactNode }) => {
  const [wordCloudExportData, setWorCloudExportData] = useState<WordCloudExportPositionData>({});

  // For seting the word cloud data from the export renderer
  window.__setWordCloudData = (wordCloudDataFromExport: WordCloudExportPositionData) => {
    setWorCloudExportData(wordCloudDataFromExport);
  };

  return <WordCloudExportContext.Provider value={wordCloudExportData}>{children}</WordCloudExportContext.Provider>;
};

export const useWordCloudExportStore = () => {
  return useContext(WordCloudExportContext);
};

export const getAllWordCloudPositionData = async (infograph: InfographState) => {
  const wordCloudPositionData: WordCloudExportPositionData = {};

  const widgetIds = getAllPageWidgetIds(infograph);
  for (let i = 0; i < widgetIds.length; i++) {
    const widgetId = widgetIds[i];
    if (!widgetId) continue;

    const widgetType = getWidgetTypeFromId(widgetId);
    if (!widgetType) continue;
    if (widgetType !== WidgetType.WordCloudChart) continue;

    try {
      const widget = infograph.widgets[widgetId] as WordCloudWidgetData;
      const { seriesData, widthPx, heightPx, seriesMeta, dataLabels, generalOptions } = widget;
      const { colors: widgetColors, colorSteps } = seriesMeta;
      const colors = widgetColors.slice(0, colorSteps ?? COLOR_STEPS);
      const { style } = dataLabels;
      const { fontFamily, fontStyle, fontWeight } = style;
      const { padding, rotation } = generalOptions;

      const listText = seriesData[0].data.map((text: string | number) => text);
      const listValues = seriesData[1].data.map((value: string | number) => value);

      const data = getWordCloudData(listText, listValues, colors);

      // skip wordclouds without words in case of empty data
      if (!data || !data.length) continue;

      // measure the size of the wordCloud
      const wordCloudComponentProps = {
        data,
        width: widthPx,
        height: heightPx,
        font: fontFamily,
        fontStyle,
        fontWeight,
        rotate: rotation,
        padding,
      };

      const words = await calculateWordCloudPostions(wordCloudComponentProps, widgetId);
      wordCloudPositionData[widgetId] = words;
    } catch (e) {
      console.error('Error calculating word cloud positions', e);
    }
  }

  return wordCloudPositionData;
};
